import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 60_000;
const INITIAL_DELAY_MS = 60_000;
const HARD_REFRESH_PARAM = '__prometheus_update_refresh';

type VersionResponse = {
	version: string;
	buildTimestamp: string;
};

const removeHardRefreshParam = () => {
	const url = new URL(window.location.href);
	if (!url.searchParams.has(HARD_REFRESH_PARAM)) {
		return;
	}

	url.searchParams.delete(HARD_REFRESH_PARAM);
	window.history.replaceState(
		window.history.state,
		document.title,
		`${url.pathname}${url.search}${url.hash}`
	);
};

const clearServiceWorkerCaches = async () => {
	if ('caches' in window) {
		const cacheNames = await window.caches.keys();
		await Promise.all(
			cacheNames.map((cacheName) => window.caches.delete(cacheName))
		);
	}

	if ('serviceWorker' in navigator) {
		const registrations = await navigator.serviceWorker.getRegistrations();
		await Promise.all(
			registrations.map((registration) => registration.unregister())
		);
	}
};

const forceHardRefresh = async () => {
	try {
		await clearServiceWorkerCaches();
	} finally {
		const url = new URL(window.location.href);
		url.searchParams.set(HARD_REFRESH_PARAM, Date.now().toString());
		window.location.replace(url.toString());
	}
};

export function useUpdateChecker() {
	const [updateAvailable, setUpdateAvailable] = useState(false);
	const clientBuildTimestamp = useRef(__BUILD_TIMESTAMP__);

	const checkWebVersion = useCallback(async () => {
		try {
			const response = await fetch(`/version.json?t=${Date.now()}`, {
				cache: 'no-store',
				headers: {
					'Cache-Control': 'no-cache',
				},
			});

			if (!response.ok) {
				return;
			}

			const version = (await response.json()) as VersionResponse;

			if (
				version.buildTimestamp &&
				version.buildTimestamp !== clientBuildTimestamp.current
			) {
				setUpdateAvailable(true);
			}
		} catch {
			// Version checks should never interrupt the app.
		}
	}, []);

	useEffect(() => {
		removeHardRefreshParam();
	}, []);

	useEffect(() => {
		let intervalId: ReturnType<typeof setInterval> | undefined;
		const initialTimer = setTimeout(() => {
			void checkWebVersion();
			intervalId = setInterval(() => {
				void checkWebVersion();
			}, POLL_INTERVAL_MS);
		}, INITIAL_DELAY_MS);

		return () => {
			clearTimeout(initialTimer);
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, [checkWebVersion]);

	const applyUpdate = useCallback(() => {
		void forceHardRefresh();
	}, []);

	return {
		updateAvailable,
		applyUpdate,
		currentVersion: __APP_VERSION__,
	};
}
